import { Combobox } from "@/components/flexkit/combobox/Combobox";
import { Avatar, AvatarFallback, AvatarImage, AvatarInitials } from "@/components/Avatar";
import { ServiceAccountAvatar } from "@/components/settings/organization/service-accounts/ServiceAccountAvatar";
import { useMembers } from "@/queries/organization-queries";
import { useListServiceAccounts } from "@/queries/service-account-queries";
import { Principal } from "gitpod-next-api/gitpod/v1/identity_pb";
import { useMemo, type FC } from "react";

export const ALL_CREATORS_VALUE = "all";
export type CreatorFilterValue = string;

type AutomationCreatorFilterProps = {
    value: CreatorFilterValue;
    onChange: (value: CreatorFilterValue) => void;
};

type CreatorOption = {
    id: string;
    name: string;
    avatarUrl?: string;
    principal: Principal;
};

const ALL_CREATORS_OPTION: CreatorOption = {
    id: ALL_CREATORS_VALUE,
    name: "Everyone",
    principal: Principal.UNSPECIFIED,
};

const CreatorAvatar: FC<{ option: CreatorOption }> = ({ option }) => {
    if (option.principal === Principal.USER) {
        return (
            <Avatar size={16}>
                {option.avatarUrl && <AvatarImage src={option.avatarUrl} alt={`${option.name}'s avatar`} />}
                <AvatarFallback>
                    <AvatarInitials name={option.name} size={16} />
                </AvatarFallback>
            </Avatar>
        );
    }

    if (option.principal === Principal.SERVICE_ACCOUNT) {
        return <ServiceAccountAvatar id={option.id} size={16} />;
    }

    return null;
};

export const AutomationCreatorFilter: FC<AutomationCreatorFilterProps> = ({ value, onChange }) => {
    const { data: membersData } = useMembers();
    const { data: serviceAccountsData } = useListServiceAccounts();

    const creatorOptions = useMemo(() => {
        const options: CreatorOption[] = [ALL_CREATORS_OPTION];

        // Add all user members
        if (membersData?.members) {
            membersData.members.forEach((member) => {
                options.push({
                    id: member.userId,
                    name: member.fullName,
                    avatarUrl: member.avatarUrl,
                    principal: Principal.USER,
                });
            });
        }

        // Add all service accounts
        if (serviceAccountsData?.serviceAccounts) {
            serviceAccountsData.serviceAccounts.forEach((sa) => {
                options.push({
                    id: sa.id,
                    name: sa.name,
                    principal: Principal.SERVICE_ACCOUNT,
                });
            });
        }

        // Sort by name (keeping "Everyone" first)
        return [options[0], ...options.slice(1).sort((a, b) => a.name.localeCompare(b.name))];
    }, [membersData, serviceAccountsData]);

    const selectedOption = creatorOptions.find((opt) => opt.id === value) || ALL_CREATORS_OPTION;

    return (
        <Combobox
            value={selectedOption.id}
            onValueChange={onChange}
            className="min-w-44 grow"
            filterPlaceholder="Search people"
        >
            <Combobox.Value>
                {() => (
                    <div className="flex min-w-0 items-center gap-1.5">
                        <CreatorAvatar option={selectedOption} />
                        <Combobox.ValueLabel className="min-w-0">{selectedOption.name}</Combobox.ValueLabel>
                    </div>
                )}
            </Combobox.Value>
            <Combobox.Popover sameWidth={false} className="min-w-[var(--popover-anchor-width)] max-w-xs">
                <Combobox.List
                    items={creatorOptions}
                    searchKeys={["name"]}
                    noMatchesComponent={<Combobox.Empty>No creators found</Combobox.Empty>}
                >
                    {(option) => (
                        <Combobox.ListItem key={option.id} value={option.id}>
                            {option.principal !== Principal.UNSPECIFIED ? (
                                <Combobox.ListItemLeadingIcon>
                                    <CreatorAvatar option={option} />
                                </Combobox.ListItemLeadingIcon>
                            ) : null}
                            <Combobox.ListItemTitle>{option.name}</Combobox.ListItemTitle>
                        </Combobox.ListItem>
                    )}
                </Combobox.List>
            </Combobox.Popover>
        </Combobox>
    );
};
